import Vue from "vue";
import { mapState } from "vuex";
import type { IStoreState } from "@/store";
import nameof from "ts-nameof.macro";
import { ExpirableActionPayload } from "@/expirableActionManager";
import { MINIMUM_PRODUCTS_FOR_DISPLAY_SHOP } from "@/consts";
import { CursorPaginationResultCompanyMemberApplyRequestViewModel } from "@/apis/model/CursorPaginationResultCompanyMemberApplyRequestViewModel";
import { CursorPaginationResultCompanyMemberInviteRequestViewModel } from "@/apis/model/CursorPaginationResultCompanyMemberInviteRequestViewModel";

const NOTIFICATION_COUNT = "_notificationCount_";
const BROADCAST_COUNT = "_broadcastCount_";
const PRODUCT_COUNT = "_productCount_";
const PRODUCT_BADGE_ALERT_STATE = "_productBadgeAlertState_";
const LAST_BROADCAST_ID = "_lastBroadcastId_";
const LAST_NOTIFICATION_ID = "_lastNotificationId_";
const INVITING_COMPANIES_COUNT = "_invitingCompaniesCount_";
const LAST_INVITING_COMPANY_ID = "_lastInvitingCompanyId_";
const INCOMMING_COMPANY_MEMBER_APPLICATION_COUNT = "_incommingCompanyMemberApplicationCount_";
const LAST_INCOMMING_COMPANY_MEMBER_APPLICATION_ID = "_lastIncommingCompanyMemberApplicationId_";
const APPLY_REQUEST_COUNT = "_applyRequestCount_";
const LAST_APPLY_REQUEST_ID = "_lastApplyRequestId_";

// see https://forum.vuejs.org/t/using-mixins-with-typescript/24256/6
const BadgeInfoMixin = Vue.extend({
  computed: {
    ...mapState({
      [NOTIFICATION_COUNT]: (state: IStoreState) => state.notificationCount,
      [BROADCAST_COUNT]: (state: IStoreState) => state.broadcastCount,
      [PRODUCT_COUNT]: (state: IStoreState) => state.productCount,
      [PRODUCT_BADGE_ALERT_STATE]: (state: IStoreState) => state.productBadgeAlertState,
      [LAST_BROADCAST_ID]: (state: IStoreState) => state.lastBroadcastId,
      [LAST_NOTIFICATION_ID]: (state: IStoreState) => state.lastNotificationId,
      [INVITING_COMPANIES_COUNT]: (state: IStoreState) => state.invitingCompaniesCount,
      [LAST_INVITING_COMPANY_ID]: (state: IStoreState) => state.lastInvitingCompanyId,
      [APPLY_REQUEST_COUNT]: (state: IStoreState) => state.applyRequestCount,
      [LAST_APPLY_REQUEST_ID]: (state: IStoreState) => state.lastApplyRequestId,
      [INCOMMING_COMPANY_MEMBER_APPLICATION_COUNT]: (state: IStoreState) =>
        state.incommingCompanyMemberApplicationCount,
      [LAST_INCOMMING_COMPANY_MEMBER_APPLICATION_ID]: (state: IStoreState) =>
        state.lastIncommingCompanyMemberApplicationId,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any),
    invitingCompaniesCount(): number {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return ((this as any)[INVITING_COMPANIES_COUNT] as number | undefined) || 0;
    },
    lastInvitingCompanyId(): number | undefined {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (this as any)[LAST_INVITING_COMPANY_ID] as number | undefined;
    },
    incommingCompanyMemberApplicationCount(): number {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const count = ((this as any)[INCOMMING_COMPANY_MEMBER_APPLICATION_COUNT] as number | undefined) || 0;
      return count;
    },
    lastIncommingCompanyMemberApplicationId(): number | undefined {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (this as any)[LAST_INCOMMING_COMPANY_MEMBER_APPLICATION_ID] as number | undefined;
    },
    applyRequestCount(): number {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return ((this as any)[APPLY_REQUEST_COUNT] as number | undefined) || 0;
    },
    lastApplyRequestId(): number | undefined {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (this as any)[LAST_APPLY_REQUEST_ID] as number | undefined;
    },
    notificationCount(): number {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return ((this as any)[NOTIFICATION_COUNT] as number | undefined) || 0;
    },
    lastNotificationId(): number | undefined {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (this as any)[LAST_NOTIFICATION_ID] as number | undefined;
    },
    broadcastCount(): number {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return ((this as any)[BROADCAST_COUNT] as number | undefined) || 0;
    },
    lastBroadcastId(): number | undefined {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (this as any)[LAST_BROADCAST_ID] as number | undefined;
    },
    totalNotificationCount(): number {
      return this.notificationCount + this.broadcastCount;
    },
    totalApplyInviteRequestCount(): number {
      return this.applyRequestCount + this.invitingCompaniesCount;
    },
    productCount(): number | undefined {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const count = (this as any)[PRODUCT_COUNT] as number | undefined;
      return count;
    },
    hasEnoughProductsForCompany(): boolean {
      if (!this.productCount) return false;
      if (this.productCount && this.productCount >= MINIMUM_PRODUCTS_FOR_DISPLAY_SHOP) return true;
      return false;
    },
    shouldShowProductBadgeAlert(): boolean {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const productBadgeAlertState = (this as any)[PRODUCT_BADGE_ALERT_STATE] as
        | ExpirableActionPayload<boolean>
        | undefined;
      const hasNotEnoughProducts = typeof this.productCount === "number" && this.productCount < 4;
      const productBadgeAlertStateFalsy =
        !productBadgeAlertState || (productBadgeAlertState && productBadgeAlertState.value === false);
      if (hasNotEnoughProducts && productBadgeAlertStateFalsy) {
        return true;
      } else {
        return false;
      }
    },
  },
  watch: {
    [nameof<IStoreState>((s) => s.incommingCompanyMemberApplicationCount)](
      value: CursorPaginationResultCompanyMemberApplyRequestViewModel | undefined,
    ) {
      //console.log("incommingCompanyMemberApplications changed", value);
    },
    [nameof<IStoreState>((s) => s.applyRequestCount)](
      value: CursorPaginationResultCompanyMemberApplyRequestViewModel | undefined,
    ) {
      console.log("myApplyRequests changed", value);
    },
    [nameof<IStoreState>((s) => s.invitingCompaniesCount)](
      value: CursorPaginationResultCompanyMemberInviteRequestViewModel | undefined,
    ) {
      console.log("myinvitingCompanies changed", value);
    },
  },
});

export default BadgeInfoMixin;
